import React, { useState } from "react"
import {
  Badge,
  Button,
  DialogContentText,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  useTheme
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { ConfirmationDialog } from "@carrotfertility/carotene-core-x"
import { Call, Device } from "@twilio/voice-sdk"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { formatCallDuration, useCallTimer } from "#/components/talk-to-carrot/VoiceWidget/useCallTimer"
import { useClosedCall } from "#/components/talk-to-carrot/VoiceWidget/useClosedCall"
import { useDeviceInitialization } from "#/components/talk-to-carrot/VoiceWidget/useDeviceInitialization"

export type TwilioTokenResponse = {
  token: string
}

export function TwilioVoiceWidget({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }): JSX.Element {
  const theme = useTheme()
  const [device, setDevice] = useState<Device | null>(null)
  const [currentCall, setCurrentCall] = useState<any>(null)
  const [callDuration, setCallDuration] = useState<number>(0)
  const intl = useIntl()
  const { employeeId } = useCurrentUser()
  const [recordCallConsent, setRecordCallConsent] = useState<boolean>(true)
  useDeviceInitialization(open, setDevice)
  useCallTimer(currentCall, setCallDuration)
  useClosedCall(currentCall, setCurrentCall)

  async function makeCall(): Promise<void> {
    if (device) {
      const params = { employeeId: employeeId.toString(), recordCallConsent: recordCallConsent.toString() }
      const call: Call = await device.connect({ params })
      setCurrentCall(call)
    }
  }

  const primaryAction = currentCall ? (
    <Button
      color="error"
      onClick={() => {
        if (currentCall) {
          currentCall.disconnect()
          setCurrentCall(null)
        }
      }}
    >
      <FormattedMessage defaultMessage="Hang up" />
    </Button>
  ) : (
    <Button onClick={makeCall}>
      <FormattedMessage defaultMessage="Call now" />
    </Button>
  )
  const secondaryAction = !currentCall && (
    <Button variant="outlined" color="secondary" onClick={() => setOpen(false)}>
      <FormattedMessage defaultMessage="Cancel" />
    </Button>
  )

  return (
    <ConfirmationDialog
      title={intl.formatMessage({ defaultMessage: "Call us" })}
      open={open}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    >
      <Box sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) })}>
        {currentCall ? (
          <Box>
            <Typography sx={{ fontWeight: "bold" }}>
              <FormattedMessage defaultMessage="Call in progress" />
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({ margin: theme.spacing(theme.tokens.spacing.sm, theme.tokens.spacing.none) })}
            >
              {formatCallDuration(callDuration)}
            </Typography>
          </Box>
        ) : (
          <Badge color="success" variant="dot">
            <Typography sx={{ fontWeight: "bold" }}>
              <FormattedMessage defaultMessage="We're online" />
            </Typography>
          </Badge>
        )}

        <DialogContentText sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.md) })}>
          {currentCall ? (
            <FormattedMessage defaultMessage="Keep your browser open to stay connected. Closing your browser ends the call." />
          ) : (
            <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
              <FormattedMessage defaultMessage="Starting a call will begin a conversation with the next available agent." />
              <FormControlLabel
                label={<FormattedMessage defaultMessage="I consent to this call being recorded." />}
                control={
                  <Checkbox checked={recordCallConsent} onChange={(e) => setRecordCallConsent(e.target.checked)} />
                }
              />
            </Stack>
          )}
        </DialogContentText>
      </Box>
    </ConfirmationDialog>
  )
}
